import { useMemo, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { MAX_COMPARE_SCENARIOS } from "@shared/constants";
import { diffModels } from "@shared/roi/diff";
import { toScenarioModel, type CompareEntry } from "@shared/schemas/scenario";
import { Button } from "@client/components/Button";
import { CompareMatrix } from "@client/components/CompareMatrix";
import { DiffTable } from "@client/components/DiffTable";
import { EmptyState } from "@client/components/EmptyState";
import { SelectField } from "@client/components/Field";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { CompareNetChart } from "@client/components/outputs/CompareNetChart";
import { useCompareScenarios } from "@client/hooks/useScenarioVersions";
import { compareSearch, parseCompareIds } from "@client/lib/compareIds";
import { packageColorVar } from "@client/lib/packageColors";

const route = getRouteApi("/_authed/compare");

/**
 * Two to four scenarios side by side — docs/08-frontend.md#comparison-compare.
 *
 * The selection lives in the URL, so a comparison is a link somebody can paste
 * into a message. Everything else is derived: the matrix, the overlaid net lines
 * and the differing-inputs table all come from one `POST /scenarios/compare`.
 *
 * The inputs table is a *pair* at a time rather than a four-way grid. A diff of
 * four models has no honest layout — every cell would need three comparisons
 * read against each other — so the page asks which two, defaults to the first
 * two, and says what it is showing.
 */
export function ScenarioComparePage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const ids = useMemo(() => parseCompareIds(search.ids), [search.ids]);
  const comparison = useCompareScenarios(ids);

  const drop = (id: string) => {
    void navigate({
      to: "/compare",
      search: compareSearch(ids.filter((candidate) => candidate !== id)),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink">Compare scenarios</h1>
        <p className="max-w-prose text-sm text-ink-soft">
          Up to {MAX_COMPARE_SCENARIOS} scenarios, from one product or several. The address bar
          holds the selection, so this page is a link you can send to somebody.
        </p>
      </header>

      {ids.length < 2 && (
        <EmptyState
          title={ids.length === 0 ? "Nothing to compare yet" : "One scenario is not a comparison"}
          description="Open a product, tick the scenarios you want to weigh against each other in the list, and press Compare. Two at a time is the useful minimum."
          action={
            <Button size="sm" onClick={() => void navigate({ to: "/" })}>
              Go to products
            </Button>
          }
        />
      )}

      {comparison.isPending && ids.length >= 2 && (
        <LoadingBlock rows={5} label="Loading the comparison" />
      )}
      {comparison.isError && (
        <ErrorNotice error={comparison.error} onRetry={() => void comparison.refetch()} />
      )}

      {comparison.isSuccess && (
        <Comparison
          entries={comparison.data.scenarios}
          currencyMismatch={comparison.data.currencyMismatch}
          onDrop={drop}
        />
      )}
    </div>
  );
}

function Comparison({
  entries,
  currencyMismatch,
  onDrop,
}: {
  entries: readonly CompareEntry[];
  currencyMismatch: boolean;
  onDrop: (id: string) => void;
}) {
  // The package ramp is this app's only categorical palette, and four scenarios
  // need four hues that are already known to be distinguishable in both themes.
  const series = entries.map((entry, index) => ({
    id: entry.id,
    name: entry.name,
    color: packageColorVar(index),
    points: entry.series.map((year) => year.cumNet),
  }));

  const horizonYears = Math.max(...entries.map((entry) => entry.horizonYears));

  const [leftId, setLeftId] = useState(entries[0]?.id ?? "");
  const [rightId, setRightId] = useState(entries[1]?.id ?? "");
  const left = entries.find((entry) => entry.id === leftId) ?? entries[0]!;
  const right = entries.find((entry) => entry.id === rightId) ?? entries[1] ?? entries[0]!;

  const diff = useMemo(
    () =>
      diffModels(
        toScenarioModel(left.model, left.model.currencyCode),
        toScenarioModel(right.model, right.model.currencyCode),
      ),
    [left, right],
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {entries.map((entry, index) => (
          <span
            key={entry.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-chip-bg py-1 pl-2.5 pr-1 text-xs font-semibold text-ink"
          >
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: packageColorVar(index) }}
            />
            {entry.name}
            <button
              type="button"
              onClick={() => onDrop(entry.id)}
              aria-label={`Remove ${entry.name} from the comparison`}
              className="rounded-full px-1.5 text-ink-soft hover:bg-link-bg-hover hover:text-ink"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {currencyMismatch && (
        <p
          role="status"
          className="rounded-lg border px-4 py-3 text-sm text-ink"
          style={{ borderColor: "var(--chart-warn)" }}
        >
          <span className="font-semibold">These scenarios price in different currencies.</span> The
          shapes are still worth reading against each other — when payback lands, how steep the
          climb is — but the totals are not, and nothing here converts between them.
        </p>
      )}

      <Panel
        title="Key figures"
        description="Recomputed for this comparison, not read from each scenario's last save."
      >
        <CompareMatrix entries={entries} comparable={!currencyMismatch} />
      </Panel>

      <Panel
        title="Cumulative net position"
        description="Where each line crosses zero is when that scenario has paid for itself."
      >
        <CompareNetChart
          series={series}
          horizonYears={horizonYears}
          currencyCode={entries[0]?.currencyCode ?? "USD"}
        />
      </Panel>

      <Panel
        title="Where the inputs differ"
        description="Two at a time, because a four-way field diff has no honest layout."
        actions={
          entries.length > 2 && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-44">
                <SelectField
                  label="Compare"
                  value={left.id}
                  onChange={(event) => setLeftId(event.target.value)}
                >
                  {entries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div className="w-44">
                <SelectField
                  label="With"
                  value={right.id}
                  onChange={(event) => setRightId(event.target.value)}
                >
                  {entries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
          )
        }
      >
        {left.id === right.id ? (
          <p className="text-sm text-ink-soft">
            Pick two different scenarios to see what separates them.
          </p>
        ) : (
          <DiffTable
            diff={diff}
            currencyCode={left.currencyCode}
            beforeLabel={left.name}
            afterLabel={right.name}
            emptyMessage="Every input matches. These two scenarios are the same model under two names."
          />
        )}
      </Panel>
    </>
  );
}
